import React, { useRef } from "react";
import { useStaggeredReveal } from "@/hooks/useStaggeredReveal";
import { Sparkles } from "lucide-react";
import speakersBg from "../../assets/speakers-bg.webp";

const REVEAL = { threshold: 0.2, stepMs: 130 };

const SPEAKERS = [
  {
    initials: "AT",
    featured: false,
    photo: "/spk-1.jpg",
    logo: "/cpf-logo.png", // White CPF Board mark (approved by CPF Board)
    logoSize: 26,
    label: "CPF Board · Official perspective",
    name: "Angeline Teo",
    role: "Assistant Director, Outreach & Partnerships · Central Provident Fund Board",
    rnf: null,
    bio: "Angeline is an Assistant Director from the Communications & Engagement Group and works on initiatives and programmes to educate and engage CPF members to plan for a secure retirement, with CPF as the foundation. She is dedicated to empowering individuals to make well-informed decisions that can positively impact their lives and the lives of their loved ones.",
    status: null,
  },
  {
    initials: "NL",
    featured: true,
    photo: "/spk-2.jpg",
    logo: "/affinity-mark.png",
    logoSize: 14,
    label: "FA Representative · Affinity Speaker",
    name: "Nicholas Lee",
    role: "Senior Associate Director (Financial Services) representing Infinity Financial Advisory",
    rnf: "RNF: LCW300188187",
    bio: "Nicholas Lee is a seasoned Financial Services Manager with over 12 years of experience in financial management and a consistent Million Dollar Round Table (MDRT) member from 2018 to 2026. A Certified Financial Planner, he is known for helping individuals and families build financial confidence and harmony through personalized strategies. Nicholas is also a strong advocate for financial literacy, sharing insights across platforms including NUS and the media. Passionate about empowering others, he is committed to helping families build strong financial foundations that last for generations.",
    status: null,
  },
  {
    initials: "ST",
    featured: false,
    photo: "/suyin.jpg?v=2",
    logo: "/capitalgroup-logo.png",
    logoSize: 16,
    badge: "Bonus Session",
    label: "Capital Group · Bonus Sharing",
    name: "Suyin Tan",
    role: "Head of Financial Intermediaries, Capital Group",
    rnf: null,
    bio: "Suyin Tan is Head of Financial Intermediaries at Capital Group, leading business development for the financial intermediaries segment across consumer and private banks, insurance companies and digital platforms in Singapore and Southeast Asia. She has 27 years of industry experience and has been with Capital Group for four years. Prior to joining Capital, Suyin worked as an Executive Director at Goldman Sachs Asset Management, and before that a Director of retail distribution at Henderson Global Investors. She holds a bachelor's degree in business from Nanyang Technological University and is based in Singapore.",
    status: null,
  },
];

const Speakers = () => {
  const rootRef = useRef(null);
  const bgFrame = useRef(null);
  const isRestricted = () =>
    typeof window !== "undefined" &&
    window.matchMedia &&
    (window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      window.matchMedia("(pointer: coarse)").matches);

  const cardRefs = useRef([]);
  const visible = useStaggeredReveal(cardRefs, SPEAKERS.length, REVEAL);

  const onCardMove = (event) => {
    if (isRestricted()) return;
    const card = event.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const rotateY = (x / rect.width - 0.5) * 5;
    const rotateX = (y / rect.height - 0.5) * -5;
    card.style.setProperty("--csl-card-x", `${x}px`);
    card.style.setProperty("--csl-card-y", `${y}px`);
    card.style.setProperty("--csl-rotate-x", `${rotateX}deg`);
    card.style.setProperty("--csl-rotate-y", `${rotateY}deg`);
  };

  const resetCard = (event) => {
    const card = event.currentTarget;
    card.style.setProperty("--csl-rotate-x", "0deg");
    card.style.setProperty("--csl-rotate-y", "0deg");
    card.style.setProperty("--csl-card-x", "50%");
    card.style.setProperty("--csl-card-y", "50%");
  };

  const onRootMove = (event) => {
    if (isRestricted()) return;
    const root = rootRef.current;
    if (!root) return;
    window.cancelAnimationFrame(bgFrame.current);
    bgFrame.current = window.requestAnimationFrame(() => {
      const rect = root.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width - 0.5) * -12;
      const y = ((event.clientY - rect.top) / rect.height - 0.5) * -8;
      root.style.setProperty("--csl-bg-x", `${x}px`);
      root.style.setProperty("--csl-bg-y", `${y}px`);
    });
  };

  const onRootLeave = () => {
    const root = rootRef.current;
    if (!root) return;
    root.style.setProperty("--csl-bg-x", "0px");
    root.style.setProperty("--csl-bg-y", "0px");
  };

  return (
    <section
      ref={rootRef}
      className="cpf-speakers-live has-js"
      id="speakers"
      aria-labelledby="cpf-speakers-title"
      data-testid="speakers-section"
      onPointerMove={onRootMove}
      onPointerLeave={onRootLeave}
    >
      <div className="csl-background" aria-hidden="true">
        <div className="csl-background__photo" />
        <div className="csl-background__veil" />
        <div className="csl-background__grain" />
        <span className="csl-background__glow csl-background__glow--gold" />
        <span className="csl-background__glow csl-background__glow--teal" />
        <span className="csl-light-beam csl-light-beam--one" />
        <span className="csl-light-beam csl-light-beam--two" />
        <span className="csl-particle-field">
          <i className="csl-particle csl-particle--1" />
          <i className="csl-particle csl-particle--2" />
          <i className="csl-particle csl-particle--3" />
          <i className="csl-particle csl-particle--4" />
        </span>
      </div>

      <div className="csl-container">
        <header className="csl-heading">
          <p className="csl-eyebrow">Speaker line-up</p>
          <h2 id="cpf-speakers-title">Learn from people who explain clearly.</h2>
          <p className="csl-heading__intro">Practical guidance, layman explanations and time for live questions.</p>
        </header>

        <div className="csl-speaker-grid">
          {SPEAKERS.map((s, i) => (
            <article
              key={s.name}
              ref={(el) => (cardRefs.current[i] = el)}
              className={`csl-speaker-card${visible.has(i) ? " is-visible" : ""}`}
              tabIndex={0}
              data-testid={`speaker-card-${i}`}
              onPointerMove={onCardMove}
              onPointerLeave={resetCard}
              onBlur={resetCard}
            >
              {s.badge && (
                <span className="csl-speaker-badge" data-testid={`speaker-badge-${i}`}>
                  <Sparkles size={11} strokeWidth={2.5} /> {s.badge}
                </span>
              )}
              <div className={`csl-speaker-avatar${s.featured ? " csl-speaker-avatar--featured" : ""}${s.photo ? " csl-speaker-avatar--photo" : ""}${s.tintPhoto ? " csl-speaker-avatar--tint" : ""}`} aria-hidden={s.photo ? undefined : "true"}>
                {s.photo ? (
                  <img className="csl-speaker-photo" src={s.photo} alt={s.name} loading="lazy" data-testid={`speaker-photo-${i}`} />
                ) : (
                  <span>{s.initials}</span>
                )}
              </div>
              <div className="csl-speaker-info">
                <p className="csl-speaker-label">{s.label}</p>
                <div className="csl-speaker-name-row">
                  <h3>{s.name}</h3>
                  {s.logo && <img className="csl-speaker-logo" src={s.logo} alt={`${s.name} organisation logo`} loading="lazy" style={s.logoSize ? { height: `${s.logoSize}px` } : undefined} data-testid={`speaker-logo-${i}`} />}
                </div>
                <span className="csl-speaker-role">{s.role}</span>
                {s.rnf && <small className="csl-speaker-rnf">{s.rnf}</small>}
                {s.bio && <p className="csl-speaker-bio">{s.bio}</p>}
                {s.status && <div className="csl-speaker-status">{s.status}</div>}
              </div>
            </article>
          ))}
        </div>

        <div className="csl-expectations" aria-label="What to expect from the speakers">
          <span>In Layman's Terms</span>
          <span>Practical retirement scenarios</span>
          <span>Live questions and answers</span>
        </div>
      </div>

      <style>{`
        .cpf-speakers-live, .cpf-speakers-live * { box-sizing: border-box; }
        .cpf-speakers-live {
          --csl-navy-950: #041522; --csl-navy-900: #071f34; --csl-navy-850: #0d2b46;
          --csl-gold-500: #d6a956; --csl-gold-400: #efc46b; --csl-gold-300: #f5d998;
          --csl-teal-500: #42c7b9; --csl-white: #ffffff; --csl-bg-x: 0px; --csl-bg-y: 0px;
          position: relative; isolation: isolate; overflow: hidden; min-height: 760px;
          padding: clamp(84px, 10vw, 132px) 0; background: var(--csl-navy-950); color: var(--csl-white);
          font-family: "Poppins", Arial, sans-serif;
        }
        .csl-background { position: absolute; z-index: -5; inset: -35px; overflow: hidden; transform: translate3d(var(--csl-bg-x), var(--csl-bg-y), 0); transition: transform 0.8s cubic-bezier(0.22, 1, 0.36, 1); pointer-events: none; }
        .csl-background__photo { position: absolute; inset: -5%; background-image: url("${speakersBg}"); background-position: center 42%; background-size: cover; filter: saturate(0.72) contrast(1.07); transform-origin: center; animation: csl-ken-burns 20s ease-in-out infinite alternate; will-change: transform; }
        .csl-background__veil { position: absolute; inset: 0; background: linear-gradient(90deg, rgba(4, 21, 34, 0.98) 0%, rgba(4, 21, 34, 0.86) 43%, rgba(4, 21, 34, 0.75) 100%), linear-gradient(180deg, rgba(4, 21, 34, 0.68) 0%, rgba(4, 21, 34, 0.18) 44%, rgba(4, 21, 34, 0.92) 100%); }
        .csl-background__grain { position: absolute; inset: 0; opacity: 0.16; background-image: radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.42) 0 0.7px, transparent 0.8px), radial-gradient(circle at 80% 60%, rgba(255, 255, 255, 0.32) 0 0.7px, transparent 0.8px); background-size: 6px 6px, 7px 7px; mix-blend-mode: soft-light; }
        .csl-background__glow { position: absolute; width: 560px; height: 560px; border-radius: 50%; filter: blur(10px); opacity: 0.48; }
        .csl-background__glow--gold { top: -270px; right: 4%; background: radial-gradient(circle, rgba(239, 196, 107, 0.26), transparent 68%); animation: csl-glow-one 12s ease-in-out infinite alternate; }
        .csl-background__glow--teal { bottom: -340px; left: 20%; background: radial-gradient(circle, rgba(66, 199, 185, 0.2), transparent 68%); animation: csl-glow-two 15s ease-in-out infinite alternate; }
        .csl-light-beam { position: absolute; top: -30%; width: 170px; height: 150%; background: linear-gradient(180deg, rgba(245, 217, 152, 0.16), transparent 72%); filter: blur(20px); opacity: 0.28; transform: rotate(18deg); transform-origin: top; }
        .csl-light-beam--one { left: 58%; animation: csl-beam-one 10s ease-in-out infinite alternate; }
        .csl-light-beam--two { left: 78%; opacity: 0.18; animation: csl-beam-two 13s ease-in-out infinite alternate; }
        .csl-particle-field { position: absolute; inset: 0; overflow: hidden; pointer-events: none; }
        .csl-particle { position: absolute; width: 5px; height: 5px; border-radius: 50%; background: rgba(245, 217, 152, 0.5); box-shadow: 0 0 12px rgba(239, 196, 107, 0.45); animation: csl-particle-rise 9s ease-in-out infinite; }
        .csl-particle--1 { left: 12%; bottom: 12%; animation-delay: -1s; }
        .csl-particle--2 { left: 37%; bottom: 7%; width: 3px; height: 3px; animation-delay: -4s; }
        .csl-particle--3 { left: 66%; bottom: 15%; width: 4px; height: 4px; animation-delay: -6s; }
        .csl-particle--4 { left: 86%; bottom: 9%; width: 3px; height: 3px; animation-delay: -2.5s; }
        .csl-container { position: relative; z-index: 2; width: min(1180px, calc(100% - 40px)); margin-inline: auto; }
        .csl-heading { max-width: 820px; margin: 0 auto clamp(44px, 6vw, 62px); text-align: center; }
        .csl-eyebrow { display: flex; align-items: center; justify-content: center; gap: 10px; margin: 0 0 15px; color: var(--csl-gold-300); font-size: 10px; font-weight: 800; letter-spacing: 0.15em; text-transform: uppercase; }
        .csl-eyebrow::before, .csl-eyebrow::after { content: ""; width: 27px; height: 1px; background: rgba(239, 196, 107, 0.56); }
        .csl-heading h2 { margin: 0; color: var(--csl-white); font-size: clamp(40px, 5.3vw, 66px); font-weight: 800; line-height: 1.06; letter-spacing: -0.047em; text-shadow: 0 8px 38px rgba(0, 0, 0, 0.3); }
        .csl-heading__intro { max-width: 670px; margin: 19px auto 0; color: rgba(255, 255, 255, 0.64); font-size: 14px; line-height: 1.75; }
        .csl-speaker-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 22px; max-width: 1040px; margin-inline: auto; perspective: 1100px; }
        .csl-speaker-card:nth-child(3):last-child { grid-column: 1 / -1; width: 100%; max-width: 509px; margin-inline: auto; }
        .csl-speaker-card { --csl-card-x: 50%; --csl-card-y: 50%; --csl-rotate-x: 0deg; --csl-rotate-y: 0deg; position: relative; display: grid; grid-template-columns: 132px 1fr; gap: 24px; min-height: 285px; padding: 26px; overflow: hidden; border: 1px solid rgba(255, 255, 255, 0.14); border-radius: 26px; background: radial-gradient(circle at var(--csl-card-x) var(--csl-card-y), rgba(239, 196, 107, 0.12), transparent 38%), linear-gradient(145deg, rgba(15, 46, 72, 0.82), rgba(4, 21, 34, 0.78)); box-shadow: 0 30px 70px rgba(0, 0, 0, 0.25); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); transform: rotateX(var(--csl-rotate-x)) rotateY(var(--csl-rotate-y)); transform-style: preserve-3d; transition: opacity 0.55s ease, transform 0.18s ease, border-color 0.25s ease, box-shadow 0.25s ease; }
        .cpf-speakers-live.has-js .csl-speaker-card:not(.is-visible) { opacity: 0; transform: translateY(22px) scale(0.985); }
        .csl-speaker-card::before { content: ""; position: absolute; inset: 0; border-radius: inherit; background: linear-gradient(115deg, transparent 24%, rgba(255, 255, 255, 0.11) 46%, transparent 66%); opacity: 0; transform: translateX(-55%); transition: opacity 0.25s ease, transform 0.55s ease; pointer-events: none; }
        .csl-speaker-card::after { content: ""; position: absolute; top: 0; right: 0; width: 70px; height: 70px; border-top: 1px solid rgba(239, 196, 107, 0.54); border-right: 1px solid rgba(239, 196, 107, 0.54); border-radius: 0 26px 0 0; opacity: 0.56; }
        .csl-speaker-badge { position: absolute; top: 0; left: 0; z-index: 6; display: inline-flex; align-items: center; gap: 5px; padding: 7px 14px 7px 12px; border-radius: 26px 0 16px 0; background: linear-gradient(135deg, #F7B838, #FFD16F); color: #071524; font-size: 9px; font-weight: 800; letter-spacing: 0.09em; text-transform: uppercase; box-shadow: 0 10px 24px rgba(0, 0, 0, 0.28); }
        .csl-speaker-card:hover, .csl-speaker-card:focus-visible { border-color: rgba(239, 196, 107, 0.52); box-shadow: 0 36px 85px rgba(0, 0, 0, 0.34); outline: none; }
        .csl-speaker-card:hover::before, .csl-speaker-card:focus-visible::before { opacity: 1; transform: translateX(55%); }
        .csl-speaker-avatar { position: relative; width: 132px; min-height: 218px; display: grid; place-items: center; overflow: hidden; border: 1px solid rgba(239, 196, 107, 0.25); border-radius: 20px; background: radial-gradient(circle at 42% 35%, rgba(66, 199, 185, 0.22), transparent 34%), linear-gradient(160deg, #173f5b, #082139); box-shadow: inset 0 0 40px rgba(0, 0, 0, 0.16); transform: translateZ(22px); }
        .csl-speaker-avatar::before, .csl-speaker-avatar::after { content: ""; position: absolute; border: 1px solid rgba(239, 196, 107, 0.16); border-radius: 50%; }
        .csl-speaker-avatar::before { width: 108px; height: 108px; animation: csl-avatar-orbit 13s linear infinite; }
        .csl-speaker-avatar::after { width: 74px; height: 74px; border-style: dashed; animation: csl-avatar-orbit-reverse 10s linear infinite; }
        .csl-speaker-avatar > span { position: relative; z-index: 2; color: var(--csl-gold-300); font-size: 26px; font-weight: 800; letter-spacing: -0.04em; text-shadow: 0 6px 24px rgba(0, 0, 0, 0.35); }
        .csl-speaker-avatar--featured { background: radial-gradient(circle at 42% 35%, rgba(239, 196, 107, 0.24), transparent 34%), linear-gradient(160deg, #284e67, #082139); }
        .csl-speaker-avatar--photo { background: #0a2136; }
        .csl-speaker-avatar--photo::before, .csl-speaker-avatar--photo::after { display: none; }
        .csl-speaker-photo { position: relative; z-index: 2; width: 100%; height: 100%; min-height: 218px; object-fit: cover; object-position: center 18%; border-radius: inherit; }
        .csl-speaker-info { position: relative; z-index: 2; align-self: center; min-width: 0; transform: translateZ(18px); }
        .csl-speaker-label { margin: 0 0 8px; color: var(--csl-gold-300); font-size: 8px; font-weight: 800; letter-spacing: 0.13em; text-transform: uppercase; }
        .csl-speaker-info h3 { margin: 0 0 5px; color: var(--csl-white); font-size: clamp(21px, 2.2vw, 28px); line-height: 1.2; letter-spacing: -0.025em; }
        .csl-speaker-name-row { display: flex; align-items: center; gap: 11px; flex-wrap: wrap; margin: 0 0 5px; }
        .csl-speaker-name-row h3 { margin: 0; }
        .csl-speaker-logo { flex: 0 0 auto; height: 30px; width: auto; display: block; }
        .csl-speaker-role, .csl-speaker-rnf { display: block; }
        .csl-speaker-role { color: rgba(255, 255, 255, 0.62); font-size: 10px; line-height: 1.5; }
        .csl-speaker-rnf { margin-top: 5px; color: rgba(255, 255, 255, 0.4); font-size: 8px; }
        .csl-speaker-bio { margin: 12px 0 0; color: rgba(255, 255, 255, 0.6); font-size: 10.5px; line-height: 1.65; }
        .csl-speaker-status { display: inline-flex; align-items: center; gap: 7px; margin-top: 17px; padding: 9px 11px; border: 1px solid rgba(239, 196, 107, 0.18); border-radius: 10px; background: rgba(239, 196, 107, 0.075); color: rgba(255, 255, 255, 0.67); font-size: 8px; line-height: 1.5; }
        .csl-speaker-status::before { content: ""; flex: 0 0 auto; width: 6px; height: 6px; border-radius: 50%; background: var(--csl-teal-500); box-shadow: 0 0 12px rgba(66, 199, 185, 0.48); }
        .csl-expectations { max-width: 880px; display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 9px; margin: 30px auto 0; }
        .csl-expectations span { display: inline-flex; align-items: center; gap: 7px; padding: 9px 12px; border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 999px; background: rgba(4, 21, 34, 0.48); color: rgba(255, 255, 255, 0.62); font-size: 8px; font-weight: 600; backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); }
        .csl-expectations span::before { content: "\\2713"; color: var(--csl-gold-300); font-weight: 800; }
        .csl-photo-credit { position: absolute; right: 18px; bottom: 13px; z-index: 3; color: rgba(255, 255, 255, 0.28); font-size: 7px; text-decoration: none; transition: color 0.2s ease; }
        .csl-photo-credit:hover, .csl-photo-credit:focus-visible { color: rgba(255, 255, 255, 0.68); outline: none; }
        @keyframes csl-ken-burns { from { transform: scale(1.06) translate3d(-1.2%, -0.5%, 0); } to { transform: scale(1.15) translate3d(1.4%, 0.9%, 0); } }
        @keyframes csl-glow-one { from { transform: translate3d(0, 0, 0) scale(0.95); } to { transform: translate3d(-70px, 55px, 0) scale(1.1); } }
        @keyframes csl-glow-two { from { transform: translate3d(0, 0, 0) scale(1.05); } to { transform: translate3d(90px, -45px, 0) scale(0.92); } }
        @keyframes csl-beam-one { from { transform: translateX(-55px) rotate(16deg); opacity: 0.16; } to { transform: translateX(65px) rotate(21deg); opacity: 0.34; } }
        @keyframes csl-beam-two { from { transform: translateX(40px) rotate(22deg); opacity: 0.12; } to { transform: translateX(-48px) rotate(16deg); opacity: 0.24; } }
        @keyframes csl-particle-rise { 0%, 100% { transform: translateY(0); opacity: 0.2; } 50% { transform: translateY(-70px); opacity: 0.72; } }
        @keyframes csl-avatar-orbit { to { transform: rotate(360deg); } }
        @keyframes csl-avatar-orbit-reverse { to { transform: rotate(-360deg); } }
        @media (max-width: 900px) {
          .csl-speaker-grid { grid-template-columns: 1fr; max-width: 680px; }
          .csl-speaker-card:nth-child(3):last-child { max-width: none; }
          .csl-background__veil { background: linear-gradient(90deg, rgba(4, 21, 34, 0.95), rgba(4, 21, 34, 0.75)), linear-gradient(180deg, rgba(4, 21, 34, 0.64), rgba(4, 21, 34, 0.9)); }
        }
        @media (max-width: 620px) {
          .cpf-speakers-live { min-height: 0; }
          .csl-container { width: min(100% - 30px, 1180px); }
          .csl-heading h2 { font-size: 40px; }
          .csl-heading__intro { font-size: 13px; }
          .csl-speaker-card { grid-template-columns: 1fr; gap: 18px; padding: 21px; border-radius: 22px; transform: none !important; }
          .csl-speaker-avatar { width: 100%; min-height: 132px; }
          .csl-speaker-avatar--photo { min-height: 340px; }
          .csl-speaker-photo { min-height: 340px; object-position: center 12%; }
          .csl-speaker-info { transform: none; }
          .csl-photo-credit { right: 10px; bottom: 7px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .cpf-speakers-live *, .cpf-speakers-live *::before, .cpf-speakers-live *::after { scroll-behavior: auto !important; animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; }
        }
      `}</style>
    </section>
  );
};

export default Speakers;
